import { BRAND, FOUNDER, SAMEAS_URLS, FOUNDER_SAMEAS_URLS, SERVICE_AREAS } from '@/data/site';

/**
 * Canonical LocalBusiness + Organization + Person JSON-LD per the
 * Ohana Implementation Brief (SCH-001). Returns a JSON-serializable
 * object. Pass it into <SeoHead jsonLd={...} />.
 *
 * `areaServedExtra` lets a city page push its specific city/county to the
 * top of the areaServed list (helps with local entity association).
 */
export function buildOrganizationJsonLd({ areaServedExtra = [] } = {}) {
  const ORG_ID = `${BRAND.origin}/#organization`;
  const FOUNDER_ID = `${BRAND.origin}/#founder`;

  const baseAreas = [
    'Columbus, IN',
    'Seymour, IN',
    'Edinburgh, IN',
    'Franklin, IN',
    'Greenwood, IN',
    'Shelbyville, IN',
    'North Vernon, IN',
    'Greensburg, IN',
    'Bloomington, IN',
    'Bartholomew County, IN',
    'Jackson County, IN',
    'Decatur County, IN',
    'Johnson County, IN',
    'Brown County, IN',
    'Shelby County, IN',
    'Jennings County, IN',
  ];

  const areaServed = [
    ...new Set([...areaServedExtra, ...baseAreas, BRAND.serviceArea]),
  ];

  const sameAs = (SAMEAS_URLS || []).filter(Boolean);
  const founderSameAs = (FOUNDER_SAMEAS_URLS || []).filter(Boolean);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['LocalBusiness', 'HomeAndConstructionBusiness'],
        '@id': ORG_ID,
        name: BRAND.legal,
        alternateName: BRAND.name,
        url: BRAND.origin,
        telephone: BRAND.phoneTel,
        email: BRAND.email,
        description:
          "Central Indiana's property transition specialists. Estate cleanouts, senior downsizing, inherited property clearing, and property preparation for families, realtors, estate executors, and property managers across Bartholomew County and central Indiana.",
        founder: {
          '@type': 'Person',
          '@id': FOUNDER_ID,
          name: FOUNDER.name,
          jobTitle: FOUNDER.jobTitle,
          alumniOf: {
            '@type': 'EducationalOrganization',
            name: FOUNDER.alumniOf,
          },
          knowsAbout: [
            'Property Transitions',
            'Estate Cleanouts',
            'Senior Downsizing',
            'Social Work',
            'Junk Removal',
          ],
          worksFor: { '@id': ORG_ID },
          ...(founderSameAs.length ? { sameAs: founderSameAs } : {}),
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: BRAND.city,
          addressRegion: BRAND.state,
          postalCode: BRAND.postalCode,
          addressCountry: 'US',
        },
        areaServed,
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Property Transition Services',
          itemListElement: [
            'Estate Cleanouts',
            'Senior Downsizing',
            'Property Cleanouts',
            'Junk Removal',
            'Foreclosure Cleanouts',
            'Realtor Property Preparation',
          ].map((name) => ({
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name },
          })),
        },
        ...(sameAs.length ? { sameAs } : {}),
      },
    ],
  };
}

/**
 * FAQPage JSON-LD builder for city + service pages.
 */
export function buildFaqJsonLd(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };
}

// Re-export to keep this module self-contained for consumers.
export { BRAND, FOUNDER, SAMEAS_URLS, FOUNDER_SAMEAS_URLS, SERVICE_AREAS };
